import Timer from './Timer';

/**
 * Used for controlling the progression of an animation depending on the users scroll position.
 * @alias Animation.Timer.ScrollTimer
 * @memberOf Animation.Timer
 * @extends Animation.Timer.Timer
 */
export default class ScrollTimer extends Timer {
  /**
   * @callback ScrollListenerCallback
   * @property {Element} scrollingElement The scrolling element.
   */
  /**
   * Adds a scroll listener.
   * @param {ScrollListenerCallback} callback closure which is called on scroll event, which has been passed through window.requestAnimationFrame.
   * @private
   * @static
   */
  static _addScrollListener(callback) {
    if (ScrollTimer._scrollListeners === undefined || ScrollTimer._scrollListeners === null) {
      /**
       * Used to store scroll listener call backs.
       * @type {ScrollListenerCallback[]}
       * @protected
       * @static
       */
      ScrollTimer._scrollListeners = [];
      document.addEventListener('scroll',
          ScrollTimer._scrollListenerFn = () => window.requestAnimationFrame(() => ScrollTimer._onScrollEvent()));
      // As the start or end point can be a function, which can depend on window size, the position will need updating on resize.
      window.addEventListener('resize', ScrollTimer._scrollListenerFn);
    }
    ScrollTimer._scrollListeners.push(callback);
  }

  static _removeScrollListener(callback) {
    if (ScrollTimer._scrollListeners !== undefined && ScrollTimer._scrollListeners !== null) {
      let i = ScrollTimer._scrollListeners.indexOf(callback);
      if (i !== -1) {
        ScrollTimer._scrollListeners.splice(i, 1);
      }
      if (ScrollTimer._scrollListeners.length <= 0) {
        ScrollTimer._onScrollEvent(); // De register event listeners.
      }
    }
  }

  static _onScrollEvent() {
    if (ScrollTimer._scrollListeners === undefined || ScrollTimer._scrollListeners === null) {
    } else if (ScrollTimer._scrollListeners.length <= 0) {
      document.removeEventListener('scroll', ScrollTimer._scrollListenerFn);
      window.removeEventListener('resize', ScrollTimer._scrollListenerFn);
      ScrollTimer._scrollListeners = null;
    } else {
      ScrollTimer._scrollListeners.forEach(scrollListener => {
        scrollListener(document.scrollingElement);
      });
    }
  }

  /**
   * @callback PointCallback Used to calculate a point when required.
   * @returns {Number}
   */
  /**
   * Used for controlling the progression of an animation depending on the users scroll position.
   * @param {boolean} xAxis is the scroll monitoring the x-axis or y-axis.
   * @param {number|PointCallback} startPoint The starting point of the animation, allows for a function which may change its result (i.e. a value that depends on browser size).
   * @param {number|PointCallback} endPoint The end point of the animation, allows for a function which may change its result (i.e. a value that depends on browser size).
   */
  constructor(xAxis = false, startPoint = 0, endPoint = 0) {
    super();
    // region Define fields.
    /**
     * If the scrolling is relative to the x axis.
     * @type {boolean}
     * @private
     */
    this._xAxis = xAxis;

    /**
     * The start point of the animation.
     * @type {Number|PointCallback}
     * @private
     */
    this._startPoint = startPoint;

    /**
     * The end scroll position, when the animation should stop.
     * @type {Number|PointCallback}
     * @private
     */
    this._endPoint = endPoint;

    /**
     * The last percent ticked... To prevent updating when no change has occurred.
     * @type {number|null}
     * @private
     */
    this._lastTickPercent = null;

    /**
     * Storage of the last request animation frame, so that it can be canceled.
     * @type {number|null}
     * @private
     */
    this._lastRequestAnimationFrame = null;

    /**
     * Storage of the scroll listener function, so that it can be removed later on.
     * @type {null | ScrollListenerCallback}
     * @private
     */
    this._scrollListenerFn = null;
    // endregion

    ScrollTimer._addScrollListener(this._scrollListenerFn = () => this.tick(0, {}));
  }

  /**
   * Get the current start point, if a function was passed it will be evaluated.
   * @returns {Number}
   * @private
   */
  _getStartPoint() {
    if (this._startPoint instanceof Function) return this._startPoint();
    return this._startPoint;
  }

  /**
   * Get the current end point, if a function was passed it will be evaluated.
   * @returns {Number}
   * @private
   */
  _getEndPoint() {
    if (this._endPoint instanceof Function) return this._endPoint();
    return this._endPoint;
  }

  setTickerCallback(callback) {
    let result = super.setTickerCallback(callback);
    this.tick(0);
    return result;
  }

  tick(p, data = {}) {
    let scrollingElement = document.scrollingElement;
    let current, total;
    if (this._xAxis) {
      current = scrollingElement.scrollLeft;
      total = scrollingElement.scrollWidth - scrollingElement.offsetWidth;
    } else {
      current = scrollingElement.scrollTop - this._getStartPoint();
      total = this._getEndPoint();
    }

    let percent = current / total;

    if (percent > 1) percent = 1;
    else if (percent < 0) percent = 0;

    if (percent === this._lastTickPercent) {
      return; // No need to do anything, the value has already been stated.
    }

    if (this.hasTickerCallback()) {
      this._lastTickPercent = percent;
    }

    super.tick(percent, {current: current, total: total});
  }
  // TODO detach fn.
};



// WEBPACK FOOTER //
// ./src/class/Animation/Timer/ScrollTimer.js